/**
 * Pure helpers for the AudioFile node's static waveform: reduce a decoded
 * AudioBuffer to a fixed number of normalised peaks, and format durations.
 */

/** Number of peaks kept per file. The canvas resamples these to its width. */
export const WAVEFORM_PEAK_COUNT = 240

/** The parts of an AudioBuffer the peak reducer reads. */
export interface PeakSource {
  numberOfChannels: number
  length: number
  getChannelData(channel: number): Float32Array
}

/**
 * Loudest absolute sample per bucket across all channels, normalised so the
 * loudest bucket is 1. Silent or empty audio yields all zeros.
 */
export function computePeaks(buffer: PeakSource, count: number = WAVEFORM_PEAK_COUNT): number[] {
  const peaks = new Array<number>(count).fill(0)
  if (buffer.length === 0 || count <= 0) return peaks

  const bucketSize = buffer.length / count
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const data = buffer.getChannelData(channel)
    for (let bucket = 0; bucket < count; bucket++) {
      const start = Math.floor(bucket * bucketSize)
      const end = Math.max(start + 1, Math.floor((bucket + 1) * bucketSize))
      let max = peaks[bucket]
      for (let i = start; i < end && i < data.length; i++) {
        const value = Math.abs(data[i])
        if (value > max) max = value
      }
      peaks[bucket] = max
    }
  }

  const loudest = Math.max(...peaks)
  return loudest > 0 ? peaks.map((peak) => peak / loudest) : peaks
}

/** `m:ss`, or `h:mm:ss` past an hour. Unknown durations render as `--:--`. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '--:--'
  const total = Math.round(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = String(total % 60).padStart(2, '0')
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${secs}` : `${minutes}:${secs}`
}
